// A2F-764: faux Word ribbon for the /pro-v2 right-pane preview.
//
// Layout matches the production Chrona Pro ribbon (manifest-pro.xml):
//
//   Planning   [IND Section Headings] [Numbered Headings ▾]
//   Formatting [Body Text] [Indentation ▾] [Caption ▾] [Table Style] [Symbols]
//   My Chrona  [My Chrona]
//
// Three of the buttons are Menu controls (chevron ▾) in the actual
// manifest — Numbered Headings, Indentation, Caption — so we render
// the chevron suffix to match. Icons are sourced from
// chrona-addin/assets/ (the same PNGs the manifest references), so
// the ghosted ribbon visually matches what the user installs.
//
// The "ghosted" feel comes from a CSS saturate filter applied to the
// whole group when ANY button is highlighted — the focused button
// then has its own opacity bumped back to 1.0 with an orange ring.

import iconSections from '../../../../assets/icon-sections-80.png?url'
import iconHeadingNum from '../../../../assets/icon-heading-num-80.png?url'
import iconBody from '../../../../assets/icon-body-80.png?url'
import iconIndent from '../../../../assets/icon-list-80.png?url'
import iconCaption from '../../../../assets/icon-figure-80.png?url'
import iconTable from '../../../../assets/icon-table-80.png?url'
import iconSymbols from '../../../../assets/icon-symbols-80.png?url'
import iconChrona from '../../../../assets/icon-80.png?url'

export type RibbonButtonId =
  | 'sections'
  | 'numbered'
  | 'body'
  | 'indentation'
  | 'caption'
  | 'table'
  | 'symbols'
  | 'mychrona'

interface ButtonDef {
  id: RibbonButtonId
  label: string // \n splits into two visual lines
  icon: string
  /** Render a small ▾ next to the label — matches production Menu controls. */
  hasMenu?: boolean
}

interface FauxWordRibbonProps {
  /** Ribbon button to highlight, or null for idle (no ring). */
  pulseId: RibbonButtonId | null
  /** Whether the IND Section Headings row is "lit" — always true today
   *  (the row is a built-in feature, not a togglable pack). Kept as a
   *  prop so the locked-on row can be made togglable later without
   *  signature churn. */
  indSectionsActive: boolean
  /** Faux filename rendered in the title bar — drives the doc-type swap. */
  filename: string
}

// Mailings dropped to make room for Chrona at the tail of the tab
// strip without overflowing the column at narrower widths. Chrona
// sits to the right of Help (Word's typical "extension tab"
// position) and renders selected — underline accent + brand-orange
// text — to match the Office UI's active-tab affordance.
const TABS = [
  'File',
  'Home',
  'Insert',
  'Draw',
  'Design',
  'Layout',
  'References',
  'Review',
  'View',
  'Help',
  'Chrona',
] as const

export function FauxWordRibbon({
  pulseId,
  indSectionsActive,
  filename,
}: FauxWordRibbonProps) {
  const groups: { title: string; items: ButtonDef[] }[] = [
    {
      title: 'Planning',
      items: [
        { id: 'sections', label: 'IND Section\nHeadings', icon: iconSections },
        { id: 'numbered', label: 'Numbered\nHeadings', icon: iconHeadingNum, hasMenu: true },
      ],
    },
    {
      title: 'Formatting',
      items: [
        { id: 'body', label: 'Body\nText', icon: iconBody },
        { id: 'indentation', label: 'Indentation', icon: iconIndent, hasMenu: true },
        { id: 'caption', label: 'Caption', icon: iconCaption, hasMenu: true },
        { id: 'table', label: 'Table\nStyle', icon: iconTable },
        { id: 'symbols', label: 'Symbols', icon: iconSymbols },
      ],
    },
    {
      title: 'My Chrona',
      items: [
        { id: 'mychrona', label: 'My\nChrona', icon: iconChrona },
      ],
    },
  ]

  const dimmed = pulseId !== null

  return (
    <div className="select-none text-brand-charcoal">
      {/* Word-blue title bar — filename centered, QAT icons left,
          avatar right. Matches the production Office title bar so the
          faux ribbon reads as part of Word, not a styled card. */}
      <div className="relative flex items-center bg-[#2B579A] px-3 py-1.5 text-[11px] text-white">
        <span className="flex items-center gap-2 z-10" aria-hidden>
          <span className="inline-block h-3.5 w-3 rounded-sm bg-white/20" />
          <span className="text-white/70">↶</span>
          <span className="text-white/70">↷</span>
        </span>
        <span className="absolute left-0 right-0 text-center font-medium truncate px-24">
          {filename} — Word
        </span>
        <span className="ml-auto flex items-center gap-2 z-10" aria-hidden>
          <span className="grid h-5 w-5 place-items-center rounded-full bg-white/20 text-[9px] font-semibold">
            YO
          </span>
        </span>
      </div>

      {/* Tab strip — Chrona renders with a brand-orange underline +
          orange text, matching how Office surfaces the active tab in
          the production UI. The other tabs stay in muted slate so
          Chrona reads as "selected" without dominating the strip. */}
      <div className="flex items-end gap-0 bg-white px-3 pt-1 border-b border-slate-200">
        {TABS.map((t) => {
          const isChrona = t === 'Chrona'
          return (
            <div
              key={t}
              className={
                isChrona
                  ? 'px-3 py-1.5 text-[11px] font-semibold text-brand-orange border-b-2 border-brand-orange -mb-px'
                  : 'px-2.5 py-1.5 text-[11px] text-slate-500'
              }
            >
              {t}
            </div>
          )
        })}
      </div>

      {/* Ribbon body */}
      <div
        className={`flex items-stretch gap-0 bg-[#FAF9F7] px-2 py-2 border-b border-slate-200 transition-[filter] duration-200 ${
          dimmed ? 'saturate-[0.7]' : ''
        }`}
      >
        {groups.map((g) => (
          <div
            key={g.title}
            className="flex flex-col items-center gap-1 px-2 border-r border-slate-200/70 last:border-r-0 min-w-[60px]"
          >
            <div className="flex items-start gap-1 flex-1">
              {g.items.map((b) => {
                const isSectionsRow = b.id === 'sections'
                const isInactive = isSectionsRow && !indSectionsActive
                return (
                  <RibbonButton
                    key={b.id}
                    def={b}
                    inactive={isInactive}
                    pulse={pulseId === b.id}
                    fade={dimmed && pulseId !== b.id}
                  />
                )
              })}
            </div>
            <div className="text-[9px] uppercase tracking-wider text-slate-400">
              {g.title}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function RibbonButton({
  def,
  inactive,
  pulse,
  fade,
}: {
  def: ButtonDef
  inactive: boolean
  pulse: boolean
  fade: boolean
}) {
  const ring = pulse
    ? 'ring-2 ring-brand-orange ring-offset-1 ring-offset-[#FAF9F7]'
    : ''
  return (
    <div
      className={`flex flex-col items-center gap-0.5 rounded w-[58px] px-1 py-1 transition-opacity ${
        fade ? 'opacity-50' : 'opacity-100'
      } ${ring}`}
    >
      <div className="h-8 w-8 grid place-items-center">
        <img
          src={def.icon}
          alt=""
          className={`h-8 w-8 object-contain ${inactive ? 'grayscale opacity-60' : ''}`}
          draggable={false}
        />
      </div>
      <div
        className={`text-[9px] leading-tight text-center whitespace-pre-line ${
          inactive ? 'text-slate-400' : 'text-brand-charcoal'
        }`}
      >
        {def.label}
        {def.hasMenu ? <span className="ml-0.5 text-slate-400">▾</span> : null}
      </div>
    </div>
  )
}
